import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';

/**
 * Back, or a named place when there is nothing of ours to go back to.
 *
 * `navigate(-1)` on the first entry of a session does nothing at all — a cold
 * deep link, a reload, a shared URL — which was survivable while the button
 * scrolled away and is not now it is pinned on screen for as long as the
 * screen is.
 *
 * Two tests, because neither is enough on its own. React Router keys the very
 * first location `'default'`, which catches the plain cold load. But a
 * `replace` mints a fresh key while staying on the same history entry, so the
 * report's week pager — which replaces so paging does not fill the history —
 * turned that test off while leaving the person exactly as stuck as before.
 * The history index it maintains alongside does not move on a replace, so
 * `idx === 0` still says "this is the first entry", whatever the key reads.
 */
function firstEntry(): boolean {
  if (typeof window === 'undefined') return false;
  const state: unknown = window.history.state;
  return typeof state === 'object' && state !== null && (state as { idx?: unknown }).idx === 0;
}

export function useGoBack(fallback = '/') {
  const navigate = useNavigate();
  const { key } = useLocation();
  const fresh = key === 'default';

  return useCallback(() => {
    if (fresh || firstEntry()) void navigate(fallback, { replace: true });
    else void navigate(-1);
  }, [fresh, navigate, fallback]);
}
